<template>
  <component
    :is="tag"
    class="app-btn"
    :class="[
      'app-btn--' + variant,
      'app-btn--' + tone,
      'app-btn--size-' + size,
      {
        'app-btn--block': block,
        'app-btn--icon-only': iconOnly,
        'app-btn--disabled': disabled,
        'app-btn--loading': loading
      }
    ]"
    :href="resolvedHref"
    :type="tag === 'button' ? type : undefined"
    :disabled="tag === 'button' ? disabled || loading : undefined"
    :aria-disabled="tag !== 'button' && (disabled || loading) ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    :aria-label="ariaLabel || undefined"
  >
    <font-awesome-icon v-if="loading" icon="spinner" class="app-btn-icon" spin />
    <font-awesome-icon v-else-if="icon && !iconAfter" :icon="icon" class="app-btn-icon" />

    <span v-if="!iconOnly" class="app-btn-label">
      <slot />
    </span>

    <!-- The spinner replaces the icon rather than sitting beside it, so a
         loading button carries exactly one glyph: a caller that passed
         icon-after would otherwise show a spinner and a decoy arrow at once.
         The label stays, because it is what says what the button does, and the
         component does not invent a "Sending..." wording over the caller's. -->
    <font-awesome-icon v-if="icon && iconAfter && !loading" :icon="icon" class="app-btn-icon" />
  </component>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The one button on the public site.

   Before this existed there were four CTA languages for the same intent: a
   stock Bootstrap .btn-primary, a frosted ghost pill on the promo bands, a
   bare span link and a bare icon anchor. On top of that six different
   .x-actions blocks each re-declared the same hover (a lift plus a blue glow)
   in its own words, and every one of them re-added its own focus ring because
   _reset.scss strips focus globally.

   Four variants cover all of them:
     primary  the filled call to action
     outline  a quieter action on a light ground
     ghost    the same action on a dark or coloured band
     link     an inline "read more" affordance, with no chrome at all

   tone is the ground the button sits on, and it only chooses the hover shadow:
   a blue glow reads on white, a black one on a dark band. Without it the same
   button needed two hand-written hover rules per page.

   The icon is a leading icon by default because that is how every CTA in the
   site is written (arrow-down toward the plans, paper-plane on a form). Set
   icon-after for the external-link cases.

   Nothing inside the button moves on hover. The icon used to nudge 3px toward
   the arrowhead and the whole button used to lift 2px, and both were removed:
   the nudge opened a visible gap between the arrow and its own label on every
   hover, and on a wide CTA the lift read as the box twitching rather than the
   surface responding. Hover is now carried by the fill, the border and a soft
   shadow, so the label and the icon stay exactly where they are. */
.app-btn {
  align-items: center;
  border: 2px solid transparent;
  border-radius: $r-sm;
  display: inline-flex;
  flex-shrink: 0;
  font-family: $headings-font-family;
  font-size: 0.95rem;
  font-weight: 600;
  gap: 0.55rem;
  justify-content: center;
  line-height: 1.2;
  padding: 0.7rem 1.35rem;
  text-align: center;
  text-decoration: none;
  transition: background-color $dur-quick $ease, border-color $dur-quick $ease,
    box-shadow $dur $ease, color $dur-quick $ease;

  /* _reset.scss strips every focus ring with !important, so whoever owns an
     interactive element has to put one back. This is the only button ring. */
  &:focus-visible {
    outline: 3px solid $ring-focus;
    outline-offset: 3px;
  }

  &.app-btn--disabled,
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.65;
  }

  &.app-btn--loading {
    cursor: progress;
    opacity: 1;
  }

  .app-btn-label {
    display: inline-block;
  }

  .app-btn-icon {
    font-size: 0.78rem;
  }
}

/* Primary ---------------------------------------------------------------- */
.app-btn--primary {
  background: $primary;
  border-color: $primary;
  color: $on-dark-strong;

  &:not(.app-btn--disabled):not([disabled]):hover,
  &:not(.app-btn--disabled):not([disabled]):focus-visible {
    background: $brand-deep;
    border-color: $brand-deep;
  }
}

.app-btn--primary.app-btn--light:not(.app-btn--disabled):not([disabled]):hover,
.app-btn--primary.app-btn--light:not(.app-btn--disabled):not([disabled]):focus-visible {
  box-shadow: $shadow-hover;
}

.app-btn--primary.app-btn--dark:not(.app-btn--disabled):not([disabled]):hover,
.app-btn--primary.app-btn--dark:not(.app-btn--disabled):not([disabled]):focus-visible {
  box-shadow: $shadow-hover-dark;
}

/* Outline ---------------------------------------------------------------- */
.app-btn--outline {
  background: transparent;
  border-color: $border-primary;
  color: $primary;

  &:not(.app-btn--disabled):not([disabled]):hover,
  &:not(.app-btn--disabled):not([disabled]):focus-visible {
    background: $tint-primary;
    border-color: $primary;
    box-shadow: $shadow-hover;
  }
}

/* Ghost: the same action on a dark or coloured band. It inverts on hover
   rather than filling with blue, because the band already carries the colour. */
.app-btn--ghost {
  background: $on-dark-fill;
  border-color: $on-dark-line-strong;
  color: $on-dark-strong;

  &:not(.app-btn--disabled):not([disabled]):hover,
  &:not(.app-btn--disabled):not([disabled]):focus-visible {
    background: $on-dark-strong;
    border-color: $on-dark-strong;
    box-shadow: $shadow-hover-dark;
    color: $text-heading;
  }
}

/* Link: no chrome, no lift. Used for "Learn more" and the big scroll arrow. */
.app-btn--link {
  background: none;
  border: 0;
  border-radius: 0;
  gap: 0.45rem;
  padding: 0;

  &:hover,
  &:focus-visible {
    box-shadow: none;
  }
}

.app-btn--link.app-btn--light {
  color: $primary;
}

.app-btn--link.app-btn--dark {
  color: $on-dark-strong;
}

/* Sizes ------------------------------------------------------------------ */
.app-btn--size-lg {
  border: 0;
  font-size: 2rem;
  gap: 0;
  padding: 0;

  .app-btn-icon {
    font-size: inherit;
  }
}

/* The size-lg link is the big scroll arrow that sits beside the highlighted
   plan's price. It idles at the muted ink so it reads as a hint next to the
   figure rather than a second call to action competing with it, and takes the
   accent on hover.

   This lives here rather than in the page because the arrow's ink is the
   button's own, the same way the other three link arrows get theirs from tone.
   The page was carrying it, and had reached for $on-dark-body, which is the
   72% white meant for type on a dark band. The arrow sits on the card's white
   surface, so that left it all but invisible. Declared after the link rules so
   it holds whichever tone the caller passes. */
.app-btn--link.app-btn--size-lg {
  color: $text-muted;

  &:hover,
  &:focus-visible {
    color: $primary;
  }
}

/* Block ------------------------------------------------------------------ */
/* A block button fills the column for the whole of the narrow band, not only for
   phones: between 576px and 767px the button still sits alone in a stacked
   column and a half-width one next to full-width copy reads as a mistake. */
@media (max-width: 767.98px) {
  .app-btn--block {
    width: 100%;
  }
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';

export default {
  components: {
    Link
  },

  props: {
    /* primary | outline | ghost | link */
    variant: {
      type: String,
      default: 'primary'
    },
    /* The ground the button sits on: light | dark. Chooses the hover shadow. */
    tone: {
      type: String,
      default: 'light'
    },
    /* md | lg */
    size: {
      type: String,
      default: 'md'
    },
    icon: {
      type: [String, Array, Object],
      default: null
    },
    iconAfter: {
      type: Boolean,
      default: false
    },
    /* An Inertia route: renders a <Link>. Takes precedence over href. */
    to: {
      type: String,
      default: ''
    },
    href: {
      type: String,
      default: ''
    },
    /* Used when there is no to / href. Let a card that is already a link render
       its affordance as a span, so the markup does not nest two links. */
    as: {
      type: String,
      default: 'button'
    },
    type: {
      type: String,
      default: 'button'
    },
    /* Swaps the leading icon for a spinner and disables the button, so a
       submitting form cannot be sent twice. Declared as a prop rather than left
       to an icon slot, because slot content renders in the parent's scope and
       would lose the button's own icon sizing. */
    loading: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: ''
    }
  },

  computed: {
    tag() {
      if (this.to) {
        return 'Link';
      }

      if (this.href) {
        return 'a';
      }

      return this.as;
    },

    /* The one destination, whichever element carries it. `Inertia Link` is an
       `<a>` that reads its target from `href` and knows nothing about `to`, so
       the `to` prop has to be handed down under that name or the Link renders an
       anchor with no target at all: `Route::inertia` pages reached through a
       `:to` button sat on a dead link. An `as="span"` affordance, which lives
       inside a card that is already a link, gets no href, which is the point of
       it. */
    resolvedHref() {
      if (this.tag === 'Link') {
        return this.to;
      }

      if (this.tag === 'a') {
        return this.href;
      }

      return undefined;
    },

    /* An icon with no label is the big scroll-arrow case, which must not
       render an empty label span. */
    iconOnly() {
      return !this.$slots.default;
    }
  }
};
</script>
